import React, { useCallback } from 'react';
import { useFormContext, useController, UseFieldArrayRemove } from 'react-hook-form';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Radio from '@mui/material/Radio';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';

import DeleteIcon from '@mui/icons-material/Delete';

import { FormTextField } from '@ts-core/core/components/FormV2';

import { MultipleChoiceExerciseInput, MultipleChoiceOptionType } from '../../types/form';

interface MultipleChoiceQuestionOptionFieldProps {
  questionIndex: number;
  index: number;

  allOptionTypes: MultipleChoiceOptionType[];
  defaultOptionType: MultipleChoiceOptionType['value'];

  onRemove: UseFieldArrayRemove;
}

const MultipleChoiceQuestionOptionField = (props: MultipleChoiceQuestionOptionFieldProps) => {
  const { questionIndex, index, onRemove, allOptionTypes, defaultOptionType } = props;
  const { control, getValues, setValue } = useFormContext<MultipleChoiceExerciseInput>(); // retrieve all hook methods
  const name = `questions.${questionIndex}.options.${index}.type`;
  const { field } = useController({
    name: `questions.${questionIndex}.options.${index}.type`,
    control,
  });
  const { value } = field;
  const allQuestionOptions = getValues(`questions.${questionIndex}.options`);

  const onChangeOptionType = useCallback(
    (newType: MultipleChoiceOptionType['value']) => {
      if (newType === 'positive') {
        allQuestionOptions.forEach((option, i) => {
          if (i !== index && option['type'] === 'positive') {
            setValue(`questions.${questionIndex}.options.${i}.type`, defaultOptionType);
          }
        });
      }
      setValue(`questions.${questionIndex}.options.${index}.type`, newType);
    },
    [index, allQuestionOptions, setValue, questionIndex, defaultOptionType]
  );

  const handleTypeSelectorChange = useCallback(
    (event: SelectChangeEvent<string>, child: React.ReactNode) => {
      const newValue = event.target.value;
      onChangeOptionType(newValue as MultipleChoiceOptionType['value']);
    },
    [onChangeOptionType]
  );

  return (
    <Stack direction="row" spacing={1} alignItems="flex-end">
      <Radio sx={{ p: 0.5 }} checked={value === 'positive'} onChange={() => onChangeOptionType('positive')} />
      <Box flexGrow={1}>
        <FormTextField control={control} name={`questions.${questionIndex}.options.${index}.text`} fullWidth />
      </Box>
      <Box>
        <FormControl variant="outlined" fullWidth size="small">
          <Select labelId={name} value={value} onChange={handleTypeSelectorChange} variant="standard" sx={{ minWidth: 100 }}>
            {allOptionTypes.map((optionType, index) => (
              <MenuItem key={optionType.value} value={optionType.value}>
                {optionType.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box width={32} height={32} display="flex" alignItems="center">
        {index > 1 && (
          <IconButton onClick={() => onRemove(index)} size="small">
            <DeleteIcon />
          </IconButton>
        )}
      </Box>
    </Stack>
  );
};

export default MultipleChoiceQuestionOptionField;
